// Grouping, ordering and sorting for the task table. No React in here — every function is
// pure, so the same code answers "what does the page look like now" and "what will it look like
// once this drop lands", and the optimistic splice can be read next to the SQL it mirrors.

import { TYPE_LABEL, moduleLabel } from './format.js';
import { LIFECYCLE_LABEL } from './lifecycle.js';

// The unsectioned bucket is a real bucket, not a placeholder: it orders and takes drops like any
// section. It just has no row in od_sections, so its id is null everywhere.
//
// It is shown ONLY when something is actually in it (see `groupTasks`). Deleting a section files
// its tasks here, and a task can still be saved with no section, so the card cannot go away
// entirely without those tasks disappearing from the only page that lists them — but in the
// normal case, where everything is filed, it is not a card and does not take up a screen.
export const UNSECTIONED = {
  id: null,
  name: 'Unsectioned',
  note: 'Tasks that have not been filed anywhere. Drag them into a section to clear this.',
};

// One definition of the columns, shared by the header rendered once above the cards and by the
// <colgroup> inside every section's table. Without a shared colgroup each table sizes its own
// columns and the same column starts at a different x in every section.
//
// The tables are `table-layout:fixed` (.o-tbl-tight), so these widths are taken literally: every
// classifying column is wide enough for its longest label on one line, and Task — the only
// column with no width — absorbs whatever is left and clips. That is the trade the widths make:
// a title is readable from its first words, a wrapped Module or Created cell costs a whole extra
// line on every row in the list.
//
// Execution mode is stored on the task but is not a column here. It describes how the work will
// be run, which is a separate module's job — the task list is read to find work, not to
// schedule it.
// One Status column, holding the lifecycle. There were two — a Lifecycle column and a Spec
// column over the legacy `status` — and they asked overlapping questions in adjacent cells.
export const COLUMNS = [
  { key: 'grip',      label: '',          width: 26 },
  { key: 'task',      label: 'Task',      width: null },
  { key: 'module',    label: 'Module',    width: 176 },
  { key: 'type',      label: 'Type',      width: 108 },
  { key: 'status',    label: 'Status',    width: 168 },
  { key: 'created',   label: 'Created',   width: 116 },
  { key: 'creator',   label: 'Created by', width: 152 },
];

export const SECTION_SORTS = [
  { id: 'manual', label: 'Manual order' },
  { id: 'name',   label: 'Name A–Z' },
  { id: 'count',  label: 'Task count' },
];

export const TASK_SORTS = [
  { id: 'manual', label: 'Manual order' },
  { id: 'newest', label: 'Newest first' },
  { id: 'oldest', label: 'Oldest first' },
  { id: 'status',     label: 'Status' },
  { id: 'rejections', label: 'Most rejected' },
  { id: 'type',   label: 'Type' },
  { id: 'module', label: 'Module' },
  { id: 'title',  label: 'Title A–Z' },
];

const sortLabel = (list, id) => list.find(s => s.id === id)?.label || id;
export const sectionSortLabel = (id) => sortLabel(SECTION_SORTS, id);
export const taskSortLabel = (id) => sortLabel(TASK_SORTS, id);

const byPosition = (a, b) => {
  const ao = a.section_order ?? Number.MAX_SAFE_INTEGER;
  const bo = b.section_order ?? Number.MAX_SAFE_INTEGER;
  if (ao !== bo) return ao - bo;
  return new Date(b.created_at || 0) - new Date(a.created_at || 0);
};

/**
 * [{ section, tasks }] — every section in the given order, then Unsectioned if it holds anything.
 *
 * Sections with no matching task still appear. Hiding them would make the list jump as filters
 * change, and would remove the only place an empty section can be dropped into. Unsectioned is
 * the exception: it is not a place anyone files work on purpose, so an empty one is a card that
 * says nothing.
 */
export function groupTasks(tasks, sections) {
  const buckets = new Map(sections.map(s => [String(s.id), []]));
  const loose = [];
  for (const task of tasks) {
    const key = task.section_id ? String(task.section_id) : null;
    const bucket = key && buckets.has(key) ? buckets.get(key) : loose;
    bucket.push(task);
  }
  const groups = sections.map(s => ({
    section: s, tasks: buckets.get(String(s.id)).sort(byPosition),
  }));
  if (loose.length) groups.push({ section: UNSECTIONED, tasks: loose.sort(byPosition) });
  return groups;
}

/**
 * The flat task list as it will be once this drop is persisted.
 *
 * Mirrors `_renumber_bucket` in repo.py: pull the task out, close the gap it left, insert it
 * above `beforeTaskId` (or at the end), then renumber that bucket densely. Keeping the two
 * within reading distance of each other is the point — if they disagree, the row visibly jumps
 * when the server answers.
 */
export function applyMove(tasks, taskId, sectionId, beforeTaskId) {
  const moved = tasks.find(t => t.id === taskId);
  if (!moved) return tasks;

  const sameBucket = (t, id) => (t.section_id || null) === (id || null);
  const target = tasks.filter(t => t.id !== taskId && sameBucket(t, sectionId)).sort(byPosition);
  const at = beforeTaskId ? target.findIndex(t => t.id === beforeTaskId) : -1;
  target.splice(at < 0 ? target.length : at, 0, { ...moved, section_id: sectionId || null });

  const source = tasks.filter(t => t.id !== taskId && sameBucket(t, moved.section_id))
    .sort(byPosition);

  const positions = new Map();
  target.forEach((t, i) => positions.set(t.id, i));
  if ((moved.section_id || null) !== (sectionId || null)) {
    source.forEach((t, i) => positions.set(t.id, i));
  }

  return tasks.map(t => {
    if (!positions.has(t.id)) return t;
    const next = { ...t, section_order: positions.get(t.id) };
    if (t.id === taskId) next.section_id = sectionId || null;
    return next;
  });
}

/** Sections in view order. `manual` is the stored order and is returned untouched. */
export function sortSections(groups, sort) {
  if (sort === 'manual') return groups;
  const [loose] = groups.filter(g => g.section.id === null);
  const named = groups.filter(g => g.section.id !== null);
  const sorted = [...named].sort((a, b) => (
    sort === 'name'
      ? a.section.name.localeCompare(b.section.name)
      : b.tasks.length - a.tasks.length
  ));
  // Unsectioned stays last under every sort. It is where deleted sections put their tasks, so a
  // list that sometimes opens with it reads as though something went wrong.
  return loose ? [...sorted, loose] : sorted;
}

const TYPE_RANK = Object.keys(TYPE_LABEL);
// Declaration order in lifecycle.js is the order work moves in, so it doubles as the sort rank.
const LIFECYCLE_RANK = Object.keys(LIFECYCLE_LABEL);
const rank = (list, value) => {
  const i = list.indexOf(value);
  return i < 0 ? list.length : i;
};

/** Tasks in view order. Always a copy — the stored order is never mutated. */
export function sortTasks(tasks, sort, options) {
  if (sort === 'manual') return tasks;
  const copy = [...tasks];
  const created = (t) => new Date(t.created_at || 0);
  switch (sort) {
    case 'newest': return copy.sort((a, b) => created(b) - created(a));
    case 'oldest': return copy.sort((a, b) => created(a) - created(b));
    case 'status': return copy.sort((a, b) =>
      rank(LIFECYCLE_RANK, a.lifecycle_status) - rank(LIFECYCLE_RANK, b.lifecycle_status));
    // Descending, unlike every other sort here: the question this one answers is "what keeps
    // coming back", and the answer is at the top or it is not an answer.
    case 'rejections': return copy.sort((a, b) =>
      Number(b.rejection_count || 0) - Number(a.rejection_count || 0));
    case 'type': return copy.sort((a, b) =>
      rank(TYPE_RANK, a.task_type) - rank(TYPE_RANK, b.task_type));
    case 'module': return copy.sort((a, b) =>
      moduleLabel(options, a.target_module).localeCompare(moduleLabel(options, b.target_module)));
    case 'title': return copy.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    default: return copy;
  }
}

// Re-exported so SectionCard can label a row without importing two modules for one string each.
export { TYPE_LABEL };
